import { useState, useEffect, useImperativeHandle, RefObject } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, AlertType } from "@components/new/Alert";
import { ButtonType } from "@components/new/Button";
import MenuItem from '@components/new/MenuItem';
import { Title2 } from "@components/new/Typo";
import ConfirmModal from "@components/modals/ConfirmModal";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { WbSelectItem, WbSelectValue } from "./WbSelectComponents";
import { Marketplace } from "@components/new/Marketplace";
import TextInputContainer from "@containers/TextInputContainer";
import SelectContainer from "@containers/SelectContainer";
import MoveModal from "../MoveModal/MoveModal";
import DelStoreModal from "../DelStoreModal/DelStoreModal";
import {
  moveSellers,
  createFbpSeller,
  updateFbpSeller,
  getSellerDetails,
  getBlockedWarehouses,
  getYmSeller,
  getWbSeller,
  getAliSeller,
  TYmSeller,
  TWbSeller,
  TAliSeller,
  TAliShippingTemplate,
  TWarehouse,
  TWarehouseSellerDetails,
  TBlockedWarehouses,
} from "@api/apiClient/fbp";
import { getSellers, getMarketplaceId, Seller } from '@api/apiClient/sellers';
import StoreItem from "./StoreItem";
import YmBadge from "./YmBadge";
import IconPlus from '@assets/icons/outline/plus.svg?react';
import SearchIcon from '@assets/icons/outline/search.svg?react';
import { FormHandle, IWBInfoItem, IYMInfoItem, IAliInfoItem } from "./types";
import AlertIcon from "@assets/icons/solid/circle-alert.svg?react";
import styles from './Form.module.scss';

export interface FormType {
  name: string;
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
  aliSellerId: string;
  aliShippingTemplateId: string;
}

const StoresSelectItem = ({ item, isSelected }: { item: TYmSeller, isSelected?: boolean }) => {
  return (
    <MenuItem
      title={item.name}
      active={isSelected}
      rightDecoration={<YmBadge placementType={item.placementType} />}
      stretch
    />
  )
}

const Form = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  warehouse,
  sellerWarehouseId,
  isNewLinkToMove,
  ymItems,
  wbItems,
  setHeaderMessage,
  onCloseWindow,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
  }) => void;
  onSuccess: ({ ymSellerIds, wbSellerIds, newSellerWarehouseName }: {
    ymSellerIds: string[];
    wbSellerIds: string[];
    newSellerWarehouseName: string;
  }) => void;
  onError: () => void;
  warehouse: TWarehouse;
  sellerWarehouseId?: string;
  ymItems?: IYMInfoItem[];
  wbItems?: IWBInfoItem[];
  isNewLinkToMove?: boolean;
  setHeaderMessage: (v: React.ReactNode) => void;
  onCloseWindow: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [wbInfos, setWbInfos] = useState<IWBInfoItem[]>(wbItems ?? []);
  const [ymSellers, setYmSellers] = useState<IYMInfoItem[]>(ymItems ?? []);
  const [aliSellers, setAliSellers] = useState<IAliInfoItem[]>([]);
  const [isOpenConfirmMoveWindow, setOpenConfirmMoveWindow] = useState<boolean>(false);
  const [isOpenDelWindow, setOpenDelWindow] = useState<boolean>(false);
  const [isMoveWindowOpen, setMoveWindowOpen] = useState<boolean>(false);
  const [ymItemsToMove, setYmItemsToMove] = useState<IYMInfoItem[]>([]);
  const [wbItemsToMove, setWbItemsToMove] = useState<IWBInfoItem[]>([]);
  const [sellerWarehouse, setSellerWarehouse] = useState<TWarehouseSellerDetails | undefined>();
  const [blockedWarehouses, setBlockedWarehouses] = useState<TBlockedWarehouses | undefined>();
  const { control, handleSubmit, watch, setValue } = useForm<FormType>({
    defaultValues: {
      name: "",
      wbId: "",
      wbSellerId: "",
      ymSellerId: "",
      aliSellerId: "",
      aliShippingTemplateId: "",
    },
    // mode: 'onBlur',
  });

  const wbId = watch("wbId");
  const wbSellerId = watch("wbSellerId");
  const ymSellerId = watch("ymSellerId");
  const aliSellerId = watch("aliSellerId");
  const aliShippingTemplateId = watch("aliShippingTemplateId");
  const name = watch("name");

  const blockedWarehousesQuery = useQuery({
    queryKey: ["getBlockedWarehouses", sellerWarehouseId ?? ""],
    queryFn: () => getBlockedWarehouses(sellerWarehouseId ?? ""),
    retry: 1,
    onSuccess: (data) => {
      setBlockedWarehouses(data);
    },
    enabled: Boolean(sellerWarehouseId),
  });

  const sellerWarehouseQuery = useQuery({
    queryKey: ["getSellerDetails", sellerWarehouseId ?? ""],
    queryFn: () => getSellerDetails(sellerWarehouseId ?? ""),
    retry: 1,
    onSuccess: (data) => {
      setSellerWarehouse(data);
    },
    enabled: Boolean(sellerWarehouseId),
  });

  const sellersQuery = useQuery({
    queryKey: ["sellers"],
    queryFn: () => getSellers(),
    retry: 1,
    select: ({ items }) => items
      .filter(s => (getMarketplaceId(s) === "Wildberries"))
  });

  const ymSellersQuery = useQuery({
    queryKey: ["getYmSeller", warehouse.id],
    queryFn: () => getYmSeller(warehouse.id),
    retry: 1,
  });

  const aliSellersQuery = useQuery({
    queryKey: ["getAliSeller", warehouse.id],
    queryFn: () => getAliSeller(warehouse.id),
    retry: 1,
    enabled: Boolean(warehouse.fbpAvailableMarketplaces.aliexpress),
  });

  const wbSellersQuery = useQuery({
    queryKey: ["getWbSeller", wbSellerId, warehouse.id],
    queryFn: () => getWbSeller({ wbSellerId, fbpWarehouseId: warehouse.id }),
    retry: 1,
    enabled: Boolean(wbSellerId),
  });

  const createMutation = useMutation({
    mutationFn: createFbpSeller,
  });

  const updateMutation = useMutation({
    mutationFn: updateFbpSeller,
  });

  const moveSellerMutation = useMutation({
    mutationFn: moveSellers,
  });

  const getIsValid = () => {
    return Boolean((wbInfos?.length || ymSellers?.length || aliSellers?.length) && name)
  }

  useEffect(() => {
    if (sellersQuery.data?.length === 1 && !wbSellerId) {
      setValue("wbSellerId", (sellersQuery.data && sellersQuery.data[0]?.wbSeller?.id) ?? "");
    }
  }, [sellersQuery.data?.length])

  useEffect(() => {
    if (wbSellersQuery.data?.length === 1 && !wbId) {
      setValue("wbId", (wbSellersQuery.data && wbSellersQuery.data[0]?.wbId) ?? "")
    }
  }, [wbSellersQuery.data?.length])

  useEffect(() => {
    if (ymSellersQuery.data?.length === 1 && !ymSellerId) {
      setValue("ymSellerId", (ymSellersQuery.data && ymSellersQuery.data[0]?.id) ?? "")
    }
  }, [ymSellersQuery.data?.length])

  useEffect(() => {
    if (aliSellersQuery.data?.aliSellers?.length === 1 && !aliSellerId) {
      setValue("aliSellerId", aliSellersQuery.data.aliSellers[0]?.id ?? "")
    }
  }, [aliSellersQuery.data?.aliSellers?.length])

  // Шаблоны доставки свои у каждого магазина — при смене магазина сбрасываем выбор
  useEffect(() => {
    setValue("aliShippingTemplateId", "");
  }, [aliSellerId])

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(
        createMutation.isLoading ||
        updateMutation.isLoading ||
        moveSellerMutation.isLoading ||
        sellerWarehouseQuery.isLoading ||
        blockedWarehousesQuery.isLoading
      ),
    });
  }, [
    sellerWarehouseQuery.isLoading,
    createMutation.isLoading,
    updateMutation.isLoading,
    moveSellerMutation.isLoading,
    wbInfos, ymSellers, aliSellers, name,
    blockedWarehousesQuery.isLoading
  ])

  useEffect(() => {
    if (sellerWarehouse) {
      setYmSellers((sellerWarehouse?.ymWarehouses ?? []).map(v => ({
        id: v.id,
        name: v.name,
        ymSellerId: "",
        isAwaitingDeletion: v.isAwaitingDeletion,
        placementType: v.placementType,
      })))
      setWbInfos((sellerWarehouse?.wbWarehouses ?? []).map(v => ({
        id: v.id,
        warehouseName: v.warehouseName,
        wbSellerId: v.wbSellerId,
        warehouseId: v.warehouseId,
        sellerName: v.sellerName,
        isSaved: true,
        tokenStatus: v?.tokenStatus,
        isAwaitingDeletion: v.isAwaitingDeletion,
        // tokenStatus: v.wbSeller?.tokenStatus,
      })))
      setAliSellers((sellerWarehouse?.aliWarehouses ?? []).map(v => ({
        id: v.id,
        name: v.name,
        aliSellerId: "",
        tokenStatus: v.tokenStatus,
        isAwaitingDeletion: v.isAwaitingDeletion,
        shippingTemplateName: v.shippingTemplateName,
      })))
      setValue("name", sellerWarehouse?.name)
    }
  }, [sellerWarehouse])

  const onSubmit = (form: FormType) => {
    const mutationSettings = {
      onSuccess: () => {
        onSuccess({
          wbSellerIds: wbInfos.map(v => v.wbSellerId),
          ymSellerIds: ymSellers.map(v => v.ymSellerId),
          newSellerWarehouseName: form.name,
        })
        if (isNewLinkToMove) {
          queryClient.invalidateQueries({ queryKey: ["getWarehouses"] })
          queryClient.invalidateQueries({ queryKey: ["getSellerDetails"] })
        }
      },
      onError: () => onError(),
    };
    if (isNewLinkToMove) {
      moveSellerMutation.mutate({
        newSellerWarehouseName: form.name,
        wbWarehouseIds: wbInfos.map(v => v.id),
        ymWarehouseIds: ymSellers.map(v => v.id),
      }, mutationSettings)
    } else if (sellerWarehouse) {
      updateMutation.mutate({
        wbInfos: wbInfos.filter(v => !v.isSaved).map(v => ({
          wbId: v.warehouseId,
          wbSellerId: v.wbSellerId,
        })),
        ymSellerIds: ymSellers.filter(v => v.ymSellerId).map(v => v.ymSellerId),
        aliInfos: aliSellers
          .filter(v => v.aliSellerId && v.shippingTemplateId)
          .map(v => ({
            aliSellerId: v.aliSellerId,
            shippingTemplateId: v.shippingTemplateId as number,
          })),
        sellerWarehouseId: sellerWarehouse.id,
        name: form.name,
      }, mutationSettings)
    } else {
      createMutation.mutate({
        wbInfos: wbInfos.map(v => ({
          wbId: v.warehouseId,
          wbSellerId: v.wbSellerId,
        })),
        ymSellerIds: ymSellers.map(v => v.ymSellerId),
        aliInfos: aliSellers
          .filter(v => v.aliSellerId && v.shippingTemplateId)
          .map(v => ({
            aliSellerId: v.aliSellerId,
            shippingTemplateId: v.shippingTemplateId as number,
          })),
        fbpWarehouseId: warehouse.id,
        name: form.name,
      }, mutationSettings)
    }

  };

  const submit = handleSubmit(onSubmit);

  useImperativeHandle(formRef, () => ({
    submit,
  }));

  const noAwailableWarehouses = Boolean(wbSellersQuery.data && !wbSellersQuery.isLoading && !wbSellersQuery.data?.length);

  const filteredWbIds = (wbSellersQuery.data ?? [])
    .filter(v => !wbInfos.find(x => ((x.warehouseId === v.wbId) && (x.wbSellerId === wbSellerId)))); // элементы уже выбранные для других магазинов делаем недоступными

  const filteredYmSellerIds = (ymSellersQuery.data ?? [])
    .filter(v => !ymSellers.find(x => (x.ymSellerId === v.id))); // элементы уже выбранные для других магазинов делаем недоступными

  const filteredAliSellerIds = (aliSellersQuery.data?.aliSellers ?? [])
    .filter(v => !aliSellers.find(x => (x.aliSellerId === v.id))); // элементы уже выбранные делаем недоступными

  // Шаблоны доставки выбранного магазина
  const aliShippingTemplates: TAliShippingTemplate[] = aliSellerId
    ? (aliSellersQuery.data?.aliSellers ?? []).find(v => v.id === aliSellerId)?.shippingTemplates ?? []
    : [];

  return (
    <form className={styles.form}>
      <TextInputContainer
        control={control}
        name="name"
        title={t("fbpPage.addWarehouseModal.name")}
        rules={{ required: t("defaultFormErrors.required") }}
        maxLength={100}
      />

      {warehouse.fbpAvailableMarketplaces.wildberries ? (
        <div className={styles.section}>
          <div className={styles.checkboxRow}>
            <div className={styles.marketplaceRow}>
              <Marketplace name="Wildberries" size="small" />
              <Title2>{t(`marketplace.Wildberries`)}</Title2>
            </div>
          </div>

          <SelectContainer<Seller, FormType>
            control={control}
            name="wbSellerId"
            placeholder={t("fbpPage.addWarehouseModal.wbStore")}
            items={sellersQuery.data ?? []}
            keyExtractor={(item: Seller) => (item.wbSeller?.id ?? "")}
            labelOptionItem={(item: Seller | null) => (item?.name ?? "")}
            searchable
            prefix={<SearchIcon />}
          />
          <SelectContainer<TWbSeller, FormType>
            control={control}
            name="wbId"
            placeholder={t("fbpPage.addWarehouseModal.warehouseId")}
            renderSelectedItem={WbSelectValue}
            items={filteredWbIds}
            keyExtractor={(item: TWbSeller) => item.wbId}
            ItemComponent={WbSelectItem}
            disabled={!wbSellerId || noAwailableWarehouses}
            hasError={noAwailableWarehouses}
            errorMessage={noAwailableWarehouses ? t("fbpPage.addWarehouseModal.noWarehouses") : undefined}
          />
          <div className={styles.rowLeft}>
            <LinkButton
              icon={IconPlus}
              size={LinkButtonSize.L}
              title={t("fbpPage.addWarehouseModal.addStore")}
              onClick={(e: any) => {
                e.preventDefault();
                const wbSeller = (sellersQuery.data || []).find(v => (v.wbSeller?.id === wbSellerId));
                setWbInfos([...wbInfos, {
                  wbSellerId,
                  id: "",
                  warehouseId: wbId,
                  warehouseName: (wbSellersQuery.data || []).find(v => (v.wbId === wbId))?.name || "",
                  sellerName: wbSeller?.name || "",
                  tokenStatus: wbSeller?.wbSeller?.tokenStatus,
                  isAwaitingDeletion: false,
                }]);
                setValue("wbId", "")
              }}
              disabled={!(wbSellerId && wbId)}
            />
          </div>

          <div className={styles.storeItems}>
            {wbInfos.map(v => {
              const blocked = (blockedWarehouses?.wbWarehouses || [])
                .find(b => (b.id === v.id));
              return (
                <StoreItem
                  key={v.warehouseId}
                  title={v.sellerName}
                  isAwaitingDeletion={v.isAwaitingDeletion}
                  subtitle={`${v.warehouseName}, ID: ${v.warehouseId}`}
                  onMoveStore={() => {
                    setOpenConfirmMoveWindow(true);
                    setWbItemsToMove([v]);
                    setYmItemsToMove([]);
                  }}
                  onDelStore={() => {
                    setOpenDelWindow(true);
                    setWbItemsToMove([v]);
                    setYmItemsToMove([]);
                  }}
                  isMovingBlocked={blocked?.isMovingBlocked}
                  isDeletionBlocked={blocked?.isDeletionBlocked}
                  isNewLinkToMove={isNewLinkToMove}
                  status={v.tokenStatus}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {warehouse.fbpAvailableMarketplaces.yandex ? (
        <div className={styles.section}>
          <div className={styles.checkboxRow}>
            <div className={styles.marketplaceRow}>
              <Marketplace name="Yandex" size="small" />
              <Title2>{t(`marketplace.Yandex`)}</Title2>
            </div>
          </div>
          <SelectContainer<TYmSeller, FormType>
            control={control}
            name="ymSellerId"
            placeholder={t("fbpPage.addWarehouseModal.ymStore")}
            items={filteredYmSellerIds}
            keyExtractor={(item: TYmSeller) => item.id}
            renderSelectedItem={({ item }: { item: (TYmSeller | null) }) => (item?.name ?? "")}
            ItemComponent={StoresSelectItem}
            searchable
            prefix={<SearchIcon />}
          />
          <div className={styles.rowLeft}>
            <LinkButton
              icon={IconPlus}
              size={LinkButtonSize.L}
              title={t("fbpPage.addWarehouseModal.addStore")}
              onClick={(e: any) => {
                e.preventDefault();
                const seller = filteredYmSellerIds.find(v => v.id === ymSellerId);
                setYmSellers([...ymSellers, {
                  id: "",
                  ymSellerId: ymSellerId,
                  name: seller?.name ?? "",
                  isAwaitingDeletion: false,
                  placementType: seller?.placementType,
                }]);
                setValue("ymSellerId", "");
              }}
              disabled={!ymSellerId}
            />
          </div>
          <div className={styles.storeItems}>
            {ymSellers.map(v => {
              const blocked = (blockedWarehouses?.ymWarehouses || [])
                .find(b => (b.id === v.id));
              return (
                <StoreItem
                  key={v.ymSellerId}
                  title={v.name}
                  placementType={v.placementType}
                  isAwaitingDeletion={v.isAwaitingDeletion}
                  subtitle={""}
                  onMoveStore={() => {
                    setYmItemsToMove([v]);
                    setWbItemsToMove([]);
                    setOpenConfirmMoveWindow(true);
                  }}
                  onDelStore={() => {
                    setYmItemsToMove([v]);
                    setWbItemsToMove([]);
                    setOpenDelWindow(true);
                  }}
                  isMovingBlocked={blocked?.isMovingBlocked}
                  isDeletionBlocked={blocked?.isDeletionBlocked}
                  isNewLinkToMove={isNewLinkToMove}
                  //hideMove={v.placementType === "Fby"}
                />
              );
            })}
          </div>
        </div>
      ) : null}

      {warehouse.fbpAvailableMarketplaces.aliexpress ? (
        <div className={styles.section}>
          <div className={styles.checkboxRow}>
            <div className={styles.marketplaceRow}>
              <Marketplace name="Aliexpress" size="small" />
              <Title2>{t(`marketplace.Aliexpress`)}</Title2>
            </div>
          </div>
          <SelectContainer<TAliSeller, FormType>
            control={control}
            name="aliSellerId"
            placeholder={t("fbpPage.addWarehouseModal.aliStore")}
            items={filteredAliSellerIds}
            keyExtractor={(item: TAliSeller) => item.id}
            labelOptionItem={(item: (TAliSeller | null)) => (item?.name ?? "")}
            searchable
            prefix={<SearchIcon />}
          />
          <SelectContainer<TAliShippingTemplate, FormType>
            control={control}
            name="aliShippingTemplateId"
            placeholder={t("fbpPage.addWarehouseModal.aliShippingTemplate")}
            items={aliShippingTemplates}
            keyExtractor={(item: TAliShippingTemplate) => String(item.id)}
            labelOptionItem={(item: (TAliShippingTemplate | null)) => (item?.name ?? "")}
          />
          <div className={styles.rowLeft}>
            <LinkButton
              icon={IconPlus}
              size={LinkButtonSize.L}
              title={t("fbpPage.addWarehouseModal.addStore")}
              onClick={(e: any) => {
                e.preventDefault();
                setAliSellers([...aliSellers, {
                  id: "",
                  aliSellerId: aliSellerId,
                  name: filteredAliSellerIds.find(v => v.id === aliSellerId)?.name ?? "",
                  shippingTemplateId: Number(aliShippingTemplateId),
                  shippingTemplateName: aliShippingTemplates
                    .find(v => String(v.id) === aliShippingTemplateId)?.name ?? "",
                  isAwaitingDeletion: false,
                }]);
                setValue("aliSellerId", "");
                setValue("aliShippingTemplateId", "");
              }}
              disabled={!aliSellerId || !aliShippingTemplateId}
            />
          </div>
          <div className={styles.storeItems}>
            {aliSellers.map(v => (
              <StoreItem
                key={v.aliSellerId || v.id}
                title={v.name}
                isAwaitingDeletion={v.isAwaitingDeletion}
                subtitle={v.shippingTemplateName}
                status={v.tokenStatus}
                // Перенос/удаление Ali-связок появится вместе с поддержкой Ali в move/delete на бэке;
                // ещё не сохранённый магазин просто убираем из списка.
                hideDel={Boolean(v.id)}
                hideMove={Boolean(v.id)}
                onMoveStore={() => {}}
                onDelStore={() => {
                  setAliSellers(aliSellers.filter(x => x.aliSellerId !== v.aliSellerId));
                }}
                isNewLinkToMove={isNewLinkToMove}
              />
            ))}
          </div>
        </div>
      ) : null}

      {isOpenConfirmMoveWindow && (
        <ConfirmModal
          title={t("fbpPage.addWarehouseModal.moveStoreConfirmTitle")}
          description={t("fbpPage.addWarehouseModal.moveStoreConfirmDescription")}
          noBtnTitle={t("fbpPage.addWarehouseModal.cancelBtn")}
          yesBtnTitle={t("fbpPage.addWarehouseModal.moveBtn")}
          icon={<AlertIcon />}
          onClose={() => setOpenConfirmMoveWindow(false)}
          requestFn={() => setMoveWindowOpen(true)}
          onSuccess={() => {}}
          type="error"
          inModal
          yesBtnType={ButtonType.ATTENTION}
        />
      )}

      {isMoveWindowOpen && (
        <MoveModal
          onClose={() => setMoveWindowOpen(false)}
          warehouse={warehouse}
          sellerFromId={sellerWarehouse?.id ?? ""}
          ymItems={ymItemsToMove}
          wbItems={wbItemsToMove}
          onError={() => {
            setMoveWindowOpen(false);
            setHeaderMessage(<Alert
              type={AlertType.ERROR}
              title={t("fbpPage.moveStoreModal.errorTitle")}
            />);
          }}
          onSuccess={(nameTo: string) => {
            sellerWarehouseQuery.refetch();
            blockedWarehousesQuery.refetch();
            setMoveWindowOpen(false);
            setHeaderMessage(<Alert
              type={AlertType.SUCCESS}
              title={t("fbpPage.moveStoreModal.successTitle", {
                name: wbItemsToMove[0]?.sellerName || ymItemsToMove[0]?.name || "",
                nameTo,
              })}
            />);
          }}
        />
      )}

      {isOpenDelWindow && (
        <DelStoreModal
          onClose={() => setOpenDelWindow(false)}
          //warehouse={warehouse}
          sellerWarehouseId={sellerWarehouse?.id ?? ""}
          ymItems={ymItemsToMove}
          wbItems={wbItemsToMove}
          onError={() => {
            setOpenDelWindow(false);
          }}
          onSuccess={() => {
            sellerWarehouseQuery.refetch();
            blockedWarehousesQuery.refetch();
            queryClient.invalidateQueries({ queryKey: ["getWarehouses"] });
            setTimeout(() => {
              sellerWarehouseQuery.refetch();
              blockedWarehousesQuery.refetch();
              queryClient.invalidateQueries({ queryKey: ["getWarehouses"] });
            }, 1000)
            setOpenDelWindow(false);
            setHeaderMessage(<Alert
              type={AlertType.NEUTRAL}
              title={t("fbpPage.delStoreModal.willBeDeleted")}
            />);
            const hasMoreItems = Boolean(
              wbInfos.find(v => (
                !(blockedWarehouses?.wbWarehouses || [])
                  .find(b => b.id === v.id)?.isDeletionBlocked
                && !wbItemsToMove.find(b => b.id === v.id)
              )) ||
              ymSellers.find(v => (
                !(blockedWarehouses?.ymWarehouses || [])
                  .find(b => b.id === v.id)?.isDeletionBlocked
                && !ymItemsToMove.find(b => b.id === v.id)
              ))
            );
            if (!hasMoreItems) {
              onCloseWindow();
            }
          }}
        />
      )}
    </form>
  )
};

export default Form;
